import { useState } from 'react';
import { Alert, Button } from '@/shared/components';
import { PermissionGuard } from '@/features/auth';
import { useDealerConfigSettingsQuery } from '../hooks/useDealerConfigSettingsQuery';
import { useDealerConfigSettingMutations } from '../hooks/useDealerConfigSettingMutations';
import { DealerConfigSettingDataTable } from './DealerConfigSettingDataTable';
import { DealerConfigSettingAddForm } from './DealerConfigSettingAddForm';
import { DealerSecretSettingPanel } from './DealerSecretSettingPanel';
import { THIRD_PARTY_KEY } from '../types/dealerSecretSetting.types';

interface Props {
    dealerId: string;
}

/** Owns its settings fetch: settings are not embedded in the dealer detail payload. */
export function DealerConfigSettings({ dealerId }: Props) {
    const [showForm, setShowForm] = useState(false);
    const { data: settings, isLoading, isError } = useDealerConfigSettingsQuery(dealerId);
    const mutations = useDealerConfigSettingMutations(dealerId);

    // Every option already linked to a setting is excluded from the add-form combobox so
    // a single-value setting can't be applied twice (mirrors smr's default exclusion; the
    // dealer catalog defines no repeatable option yet — see DealerConfigSettingDataTable's
    // by-key/by-id addressing note for what happens if one is added later).
    const appliedOptionIds = (settings ?? [])
        .filter((s) => s.config_option_id)
        .map((s) => s.config_option_id as string);

    if (isLoading) {
        return <p className="text-mp-muted text-sm">Loading config settings...</p>;
    }

    if (isError) {
        return (
            <Alert variant="error">
                Failed to load config settings.
            </Alert>
        );
    }

    const rows = (settings ?? []).filter((s) => s.configOption?.data_type !== 'secret');

    return (
        <section className="space-y-6">
            <PermissionGuard permission={{ resource: 'dealer', action: 'config-setting.browse' }}>
                <DealerSecretSettingPanel
                    dealerId={dealerId}
                    settingKey={THIRD_PARTY_KEY}
                    label="Third Party key"
                />
            </PermissionGuard>

            {rows.length === 0 ? (
                <p className="text-mp-muted text-sm">No config settings.</p>
            ) : (
                <DealerConfigSettingDataTable data={rows} mutations={mutations} />
            )}

            <div className="border-t border-mp-border pt-6 max-w-2xl">
                <h3 className="text-lg font-semibold mb-2">Config Settings</h3>
                {showForm ? (
                    <PermissionGuard permission={{ resource: 'dealer', action: 'config-setting.add' }}>
                        <DealerConfigSettingAddForm
                            mutations={mutations}
                            onDone={() => setShowForm(false)}
                            excludeOptionIds={appliedOptionIds}
                        />
                    </PermissionGuard>
                ) : (
                    <div className="flex items-start gap-4 justify-between">
                        <p className="text-sm text-mp-muted">
                            Configuration values that apply to this dealer.
                        </p>
                        <PermissionGuard permission={{ resource: 'dealer', action: 'config-setting.add' }}>
                            <Button size="sm" onClick={() => setShowForm(true)}>
                                Add Setting
                            </Button>
                        </PermissionGuard>
                    </div>
                )}
            </div>
        </section>
    );
}
